import { makeObservable, reaction, observable } from 'mobx';
import { License as BaseLicense, ADMIN_LIKE_PLANS, ALL_PLANS, Plan, excludePlans, PortalAction } from 'shared/services/license';
import { toQueryString, asArray } from 'shared/utils/utils';
import office from 'src/services/office';
import moment from 'moment';
import { AdminToolFlowsteps, DeleteFlowsteps, InsertFlowsteps, PullFlowsteps } from 'shared/utils/flowstep';
import { hasHomeButtons } from 'src/components/Home/index';
import { dataStore } from 'src/stores/index';


export const LOCKED_ICON_NAME = 'Shop';


export function getSubscribeLink(plan, subscriptionId) {
  const planId = config.portal.planIds[plan];
  return getLink({ planId, subscriptionId, action: PortalAction.Subscribe });
}

export function getManageLink(subscriptionId) {
  return getLink({ subscriptionId, action: PortalAction.Manage })
}

function getLink({ planId, subscriptionId, action }) {
  const part = action === PortalAction.Subscribe
    ? `checkout/${subscriptionId}?${toQueryString({ p: planId })}`
    : `subscriptions/${subscriptionId}`;
  return `${config.portal.url}/#/${part}`
}


export class License extends BaseLicense {

  constructor(license) {
    super(license);
    this.disposers = [];
    makeObservable(this, {
      plan: observable,
      disposers: observable,
    });
    this.disposers.push(reaction(() => dataStore?.flows, () => this.updateHomeTabFeature()));

    this._originalPlan = this.plan;
    if (this.isiOsPlatform) {
      this.plan = Plan.Admin;
    }

    this.flowsteps = {
      seeName: this.makeFeature(excludePlans([Plan.Free, Plan.Personal])),
      seeRunButton: this.makeFeature(ALL_PLANS),
      pull: {
        runAll: this.makeFeature(excludePlans([Plan.Free])),
        seeRefreshButton: this.makeFeature(ALL_PLANS),
        createOrRemove: {
          soql: this.makeFeature(ALL_PLANS),
          report: this.makeFeature(ALL_PLANS),
          csv: this.makeFeature(ALL_PLANS),
          sosl: this.makeFeature(ALL_PLANS),
          objects: this.makeFeature(ALL_PLANS)
        },
      },
      push: {
        run: this.makeFeature(excludePlans([Plan.Free])),
        createOrRemove: {
          unlimited: this.makeFeature(ADMIN_LIKE_PLANS),
          // For Personal plan only one push flow step of each type is created. Then it'll be overwritten with
          // a flowstep of the same type newly created. This process is hidden from an user
          // Therefore we don't allow a manual control over Delete, Save Copy, etc. 
          // and do not show the list: see this.pushFlowsteps.seeList
          limited: this.makeFeature([...ADMIN_LIKE_PLANS, Plan.Personal]),
        },
        massPush: {
          insert: this.makeFeature(ALL_PLANS),
          update: this.makeFeature(ALL_PLANS),
          upsert: this.makeFeature(ALL_PLANS),
          deleteOrRestore: this.makeFeature(ALL_PLANS),
        },
        editExisting: this.makeFeature(excludePlans([Plan.Free, Plan.Team])),
        seeList: this.makeFeature(excludePlans([Plan.Personal, Plan.Free])),
        seeHomeButtons: this.makeFeature(excludePlans([Plan.Team])),
      },
    };

    this.scheduler = {
      ...this.scheduler,
      seeTab: this.makeFeature(excludePlans([Plan.Team])),
    };

    this.flows = {
      custom: {
        createOrRemove: this.makeFeature(ADMIN_LIKE_PLANS),
        addFlowsteps: this.makeFeature(ADMIN_LIKE_PLANS),
      },
      seeRunAllAtHome: this.makeFeature(ALL_PLANS),
      seeTabContent: this.makeFeature(excludePlans([Plan.Free, Plan.Personal])),
      seeTab: this.makeFeature(ALL_PLANS),
      run: this.makeFeature(ALL_PLANS),
    };

    this.adminTools = {
      use: this.makeFeature(ADMIN_LIKE_PLANS),
      seeHomeButton: this.makeFeature(excludePlans([Plan.Team])),
    };

    this.home = {
      seeTab: this.makeFeature(ALL_PLANS),
    }

    this.disableFeatures();
    this.updateHomeTabFeature();
  }

  dispose() {
    this.disposers.forEach(d => d());
  }

  updateHomeTabFeature() {
    this.home.seeTab.isDisabled = !hasHomeButtons(this);
  }

  disableFeatures() {
    // console.log('Disable features: ', this.rawLicense);
    const testOptions = [
      { id: 'reports', value: false },
      { id: 'soql', value: false },
      { id: 'csv', value: false },
      { id: 'run-all-flows', value: true },
      { id: 'run-current-flow', value: false },
      { id: 'run-flow-steps', value: false },
    ];

    const { options = [] } = this.rawLicense;

    options.forEach(({ value, id }) => {
      // testOptions.forEach(({ value, id }) => {
      let feature = null;
      switch (id) {
        case 'reports': feature = this.flowsteps.pull.createOrRemove.report; break;
        case 'soql': feature = this.flowsteps.pull.createOrRemove.soql; break;
        case 'csv': feature = this.flowsteps.pull.createOrRemove.csv; break;
        case 'sosl': feature = this.flowsteps.pull.createOrRemove.sosl; break;
        case 'run-all-flows': feature = this.flows.seeRunAllAtHome; break;
        case 'run-current-flow': feature = this.flows.run; break;
        case 'refresh': feature = this.flowsteps.pull.seeRefreshButton; break;
        case 'run-flow-steps': feature = this.flowsteps.seeRunButton; break;
        case 'admin-tools': feature = this.adminTools.seeHomeButton; break;
        case 'insert': feature = this.flowsteps.push.massPush.insert; break;
        case 'update': feature = this.flowsteps.push.massPush.update; break;
        case 'upsert': feature = this.flowsteps.push.massPush.upsert; break;
        case 'delete': feature = this.flowsteps.push.massPush.deleteOrRestore; break;
      }

      if (feature) {
        feature.isDisabled = !value;
      }
    });

    const { seeRunAllAtHome, run } = this.flows;
    const { seeRunButton } = this.flowsteps;

    if (!seeRunAllAtHome.can && !run.can && !seeRunButton.can) {
      this.flows.seeTab.isDisabled = true;
    }
  }

  _checkOriginalPlan(plans) {
    return plans.includes(this._originalPlan);
  }


  get originalPlan() { return this._originalPlan; }

  //  get canChangePlan() { return this._checkOriginalPlan(ADMIN_LIKE_PLANS); }
  get hasTrueFreePlan() { return this._checkOriginalPlan([Plan.Free]); }
  get hasTrueTrialPlan() { return this._checkOriginalPlan([Plan.Trial]); }
  get hasTrueActivePlan() { return !this.hasTrueTrialPlan && !this.hasTrueFreePlan; }
  get canChangePlan() { return this.hasTrueTrialPlan && !this.isExpired; }

  get isiOsPlatform() { return office.platformType === Office.PlatformType.iOS; }
  // get isiOsPlatform() { return true; }

  // Formatted
  get remainingDays() { return Math.floor(this._remainingTimeMs > 0 ? moment.duration(this._remainingTimeMs).asDays() : 0) }
  get isExpired() { return !this.isActive; }

  // UI only
  get canSeeLicenseInformation() { return !this.isiOsPlatform; }

  isFlowstepAvailable(type) {
    if (PullFlowsteps.includes(type)) { return this.flowsteps.pull.createOrRemove[type].can; }
    if (InsertFlowsteps.includes(type)) { return this.flowsteps.push.massPush[type].can; }
    if (DeleteFlowsteps.includes(type)) { return this.flowsteps.push.massPush.deleteOrRestore.can; }
    if ([...AdminToolFlowsteps].includes(type)) { return this.adminTools.use.can }
  }
}


